import React from 'react';
import styled, {css} from 'styled-components';
import {COLORS} from '.';
import {Property} from 'csstype';
import {Size} from '../types';

interface ITypographyBase {
  $color?: string;
  lineHeight: number;
  $fontSize: number;
  $fontWeight: number;
  $fontStyle?: string;
  textTransform?: Property.TextTransform;
  textDecorationLine?: Property.TextDecorationLine;
}

export const TypographyBase = styled('div')<ITypographyBase>(({
                                                                $color,
                                                                lineHeight,
                                                                $fontSize,
                                                                $fontWeight,
                                                                $fontStyle,
                                                                textTransform,
                                                                textDecorationLine
                                                              }) => ({
  fontSize: $fontSize,
  fontWeight: $fontWeight,
  lineHeight: lineHeight + 'px',
  textTransform: textTransform ? textTransform : 'none',
  textDecorationLine: textDecorationLine ? textDecorationLine : 'none',
  fontStyle: $fontStyle ? $fontStyle : 'normal',
  color: $color ? $color : COLORS.black,
}));

enum ComponentTextStyle {
  Regular = 1,
  Bold,
  Italic,
  UppercaseBold,
}

type ComponentProps = {
  color?: string;
  className?: string;
  textStyle?: ComponentTextStyle;
  children?: React.ReactNode;
};

const componentFontWeight = (textStyle?: ComponentTextStyle) => {
  let fontWeight = 400;
  if (textStyle === ComponentTextStyle.Bold || textStyle === ComponentTextStyle.UppercaseBold) {
    fontWeight = 700;
  }
  return fontWeight;
};

const componentFontStyle = (textStyle?: ComponentTextStyle) => {
  let fontStyle = textStyle && textStyle === ComponentTextStyle.Italic ? 'italic' : '';
  return fontStyle;
};

const componentTextTransformation = (textStyle?: ComponentTextStyle): Property.TextTransform => {
  let fontStyle: Property.TextTransform = textStyle && textStyle === ComponentTextStyle.UppercaseBold ? 'uppercase' : 'none';
  return fontStyle;
};

const ComponentStyling = (fontSize: string, lineHeight: string, textStyle: ComponentTextStyle, color: string | undefined | null) => {
  return css`
    font-size: ${fontSize};
    font-weight: ${componentFontWeight(textStyle)};
    line-height: ${lineHeight};
    font-style: ${componentFontStyle(textStyle)};
    ${color === null ? '' : `color: ${color || COLORS.black};`}
  `;
};

const ComponentXLStyling = (textStyle: ComponentTextStyle, color: string | null) => ComponentStyling('24px', '28px', textStyle, color);
const ComponentXL: React.FunctionComponent<ComponentProps> = ({color, textStyle, children, className}) => {
  let fontWeight = componentFontWeight(textStyle);
  let fontStyle = componentFontStyle(textStyle);
  return (
    <TypographyBase className={className} $color={color} lineHeight={28} $fontSize={24} $fontWeight={fontWeight}
                    $fontStyle={fontStyle}>
      {children}
    </TypographyBase>
  );
};

const ComponentLStyling = (textStyle: ComponentTextStyle, color: string | null) => ComponentStyling('20px', '24px', textStyle, color);
const ComponentL: React.FunctionComponent<ComponentProps> = ({color, textStyle, children, className}) => {
  let fontWeight = componentFontWeight(textStyle);
  let fontStyle = componentFontStyle(textStyle);
  return (
    <TypographyBase className={className} $color={color} lineHeight={24} $fontSize={20} $fontWeight={fontWeight}
                    $fontStyle={fontStyle}>
      {children}
    </TypographyBase>
  );
};

const ComponentMStyling = (textStyle: ComponentTextStyle, color: string | undefined | null) => ComponentStyling('18px', '24px', textStyle, color);
const ComponentM: React.FunctionComponent<ComponentProps> = ({color, textStyle, children, className}) => {
  let fontWeight = componentFontWeight(textStyle);
  let fontStyle = componentFontStyle(textStyle);
  return (
    <TypographyBase className={className} $color={color} lineHeight={24} $fontSize={18} $fontWeight={fontWeight}
                    $fontStyle={fontStyle}>
      {children}
    </TypographyBase>
  );
};

const ComponentSStyling = (textStyle: ComponentTextStyle, color: string | null) => ComponentStyling('16px', '20px', textStyle, color);
const ComponentS: React.FunctionComponent<ComponentProps> = ({color, textStyle, children, className}) => {
  let fontWeight = componentFontWeight(textStyle);
  let fontStyle = componentFontStyle(textStyle);
  return (
    <TypographyBase className={className} $color={color} lineHeight={20} $fontSize={16} $fontWeight={fontWeight}
                    $fontStyle={fontStyle}>
      {children}
    </TypographyBase>
  );
};

const ComponentXSStyling = (textStyle: ComponentTextStyle, color: string | null) => ComponentStyling('14px', '16px', textStyle, color);
const ComponentXS: React.FunctionComponent<ComponentProps> = ({color, textStyle, children, className}) => {
  let fontWeight = componentFontWeight(textStyle);
  let fontStyle = componentFontStyle(textStyle);
  let textTransform = componentTextTransformation(textStyle);
  return (
    <TypographyBase className={className} $color={color} lineHeight={16} $fontSize={14} $fontWeight={fontWeight}
                    $fontStyle={fontStyle} textTransform={textTransform}>
      {children}
    </TypographyBase>
  );
};

const ComponentXXSStyling = (textStyle: ComponentTextStyle, color: string | null) => ComponentStyling('12px', '16px', textStyle, color);
const ComponentXXS: React.FunctionComponent<ComponentProps> = ({color, textStyle, children, className}) => {
  let fontWeight = componentFontWeight(textStyle);
  let fontStyle = componentFontStyle(textStyle);
  let textTransform = componentTextTransformation(textStyle);
  return (
    <TypographyBase className={className} $color={color} lineHeight={16} $fontSize={12} $fontWeight={fontWeight}
                    $fontStyle={fontStyle} textTransform={textTransform}>
      {children}
    </TypographyBase>
  );
};

const ComponentResponsive: React.FunctionComponent<ComponentProps & { size?: Size; width: number }> = ({
                                                                                                         color,
                                                                                                         textStyle,
                                                                                                         children,
                                                                                                         size,
                                                                                                         width
                                                                                                       }) => {
  let fontsize = size;
  if (!size) {
    if (width == -1) {
      fontsize = Size.Medium;
    } else if (width < 768) {
      fontsize = Size.Small;
    } else if (width < 992) {
      fontsize = Size.Medium;
    } else {
      fontsize = Size.Large;
    }
  }
  switch (fontsize) {
    case Size.XXSmall:
      return <ComponentXXS color={color} textStyle={textStyle} children={children}/>;
    case Size.XSmall:
      return <ComponentXS color={color} textStyle={textStyle} children={children}/>;
    case Size.Small:
      return <ComponentS color={color} textStyle={textStyle} children={children}/>;
    case Size.Medium:
      return <ComponentM color={color} textStyle={textStyle} children={children}/>;
    case Size.Large:
    default:
      return <ComponentL color={color} textStyle={textStyle} children={children}/>;
  }
};

export const Typography = css`
  html {
    font-size: 100%;
  }

  @media (min-width: 48rem) {
    html {
      font-size: 112.5%;
    }
  }

  @media (min-width: 80rem) {
    html {
      font-size: 125%;
    }
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 700;
    line-height: 120%;
    margin: 0;
  }

  h1 {
    font-size: 3rem;
  }

  h2 {
    font-size: 2.5rem;
  }

  h3 {
    font-size: 2rem;
  }

  h4 {
    font-size: 1.75rem;
  }

  h5 {
    font-size: 1.5rem;
  }

  h6 {
    font-size: 1.25rem;
  }

  p {
    font-size: 1rem;
    line-height: 150%;
    margin: 0 0 0.5rem;
  }

  p:last-of-type {
    margin-bottom: 0;
  }


  h1, h2, h3, h4, h5, h6, p, span {
    max-width: 34rem;
  }

  :lang(ja-jp) {
    h1, h2, h3, h4, h5, h6, p, span {
      max-width: 30rem;
    }
  }

  :lang(ko-kr) {
    h1, h2, h3, h4, h5, h6, p, span {
      max-width: 30rem;
    }
  }

  :lang(zh-CN) {
    h1, h2, h3, h4, h5, h6, p, span {
      max-width: 30rem;
    }
  }
`;

export const QuoteTypography = css`
  font-size: 1.5rem;
  line-height: 150%;
  margin-bottom: 0.75rem;
`;

export const NoteTypography = css`
  font-size: 0.75rem;
  line-height: 150%;
  margin-bottom: 0.375rem;
`;

export const UpperCaseTypography = css`
  font-size: 0.75rem;
  line-height: 150%;
  font-weight: 700;
  text-transform: uppercase;
`;

export const Note = styled.p`
  ${NoteTypography}
`;

export const Quote = styled.p`
  ${QuoteTypography}
`;

export const UpperCase = styled.p`
  ${UpperCaseTypography}
`;

export {
  ComponentTextStyle,
  ComponentXL,
  ComponentL,
  ComponentM,
  ComponentS,
  ComponentXS,
  ComponentXXS,
  ComponentResponsive
};
export {
  ComponentXLStyling,
  ComponentLStyling,
  ComponentMStyling,
  ComponentSStyling,
  ComponentXSStyling,
  ComponentXXSStyling
};
